import { useLocation } from '@react-router';
import { apiToSectionLayout, isValidSectionLayout } from '@shared/utils/accommodations';
import { isAccommodationsLayoutAvailable } from '@shared/utils/accommodations/layoutVisibility';
import type { AccommodationsSectionLayoutType } from '@shared/types/Accommodations.types';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { useGuestAccommodationsPageLayoutQuery } from './useResolvedAccommodationsLayoutType.graphql.generated';
import { useAccommodationsLayoutOverride } from './AccommodationsLayoutOverrideContext';

/** Resolves the active accommodations layout type (override → URL param → saved BE value → 'default'). */
export const useResolvedAccommodationsLayoutType = (eventId: string | undefined): AccommodationsSectionLayoutType => {
  const { layoutOverride } = useAccommodationsLayoutOverride();
  const location = useLocation();

  // Focused query (operation name: GuestAccommodationsPageLayout) — kept distinct from
  // GuestEventHotelRoomBlocks so refetchQueries({ include: ['GuestEventHotelRoomBlocks'] }) in
  // GuestSite.controller can't force-fire this skipped observer with an empty eventId.
  const { data } = useGuestAccommodationsPageLayoutQuery({
    variables: { eventId: eventId ?? '' },
    skip: !eventId,
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
    errorPolicy: 'all',
    ssr: false,
  });
  const savedLayout = data?.event?.travelSettings?.accommodationsPageLayout;
  const eventDesign = data?.event?.eventDesign;

  // Explicit overrides (admin preview, QA URL param) render as-is; only the persisted BE value
  // is guarded against the event's current design.
  if (layoutOverride) return layoutOverride;
  const param = new URLSearchParams(location.search).get('accommodationsLayout');
  if (isValidSectionLayout(param)) return param;

  // Fall back to 'default' when the saved layout is no longer valid for the event's design — e.g.
  // a theme change made a previously-saved `full_width_photo`/`side_by_side_elegant` incompatible.
  // The admin selector only hides the incompatible option, so the BE value can still be stale here.
  const resolved = apiToSectionLayout(savedLayout);
  const presentationLayout = (eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as { presentationLayout?: PhotoPresentationLayout } | null | undefined)
    ?.presentationLayout;
  const isAvailable = isAccommodationsLayoutAvailable(resolved, {
    layoutType: eventDesign?.websiteLayout?.layoutType,
    brannanNavigation: eventDesign?.brannanNavigation,
    presentationLayout,
  });
  return isAvailable ? resolved : 'default';
};
